import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useStore, type StoreApi } from "zustand";

import type { BroadcastComposeStore } from "../store/broadcastComposeStoreFactory";
import type { AudiencesBroadcast } from "../types";

import {
  getMarketingCampaigns,
  marketingCalendarKeys,
} from "@/api/marketing-calendar";

export const MARKETING_CALENDAR_SOURCE = "marketing_calendar";

/** Create-payload stamp for broadcasts born in the plan flow. */
export function calendarSourceStamp(
  returnTo?: string,
): { source: string } | Record<string, never> {
  return returnTo === "marketing-calendar"
    ? { source: MARKETING_CALENDAR_SOURCE }
    : {};
}

export function isCalendarOwnedBroadcast(
  broadcast: Pick<AudiencesBroadcast, "source">,
): boolean {
  return broadcast.source === MARKETING_CALENDAR_SOURCE;
}

interface UseCalendarOwnedLockArgs {
  siteId: string;
  broadcast: Pick<AudiencesBroadcast, "id" | "source">;
  storeApi: StoreApi<BroadcastComposeStore>;
}

/** Edit-page brain for a calendar-owned broadcast: resolves the owning
 * campaign into the store's scheduleWindow. Leaving a campaign happens in
 * the plan wizard, where the detach also clears the stamp server-side
 * (CON-232); the editor's own Unlink was removed with its banner (FOG-2496). */
export function useCalendarOwnedLock({
  siteId,
  broadcast,
  storeApi,
}: UseCalendarOwnedLockArgs) {
  const calendarOwned = useStore(storeApi, (s) => s.calendarOwned);

  const { data } = useQuery({
    queryKey: marketingCalendarKeys.campaigns(siteId),
    queryFn: () => getMarketingCampaigns(siteId),
    enabled: calendarOwned,
  });
  const owningCampaign = useMemo(() => {
    if (!data) return null;
    return (
      data.campaigns.find((campaign) =>
        campaign.broadcasts.some((b) => b.broadcast_id === broadcast.id),
      ) ?? null
    );
  }, [data, broadcast.id]);
  const campaignWindow = useMemo(
    () =>
      owningCampaign
        ? {
            startDate: owningCampaign.start_date,
            endDate: owningCampaign.end_date,
          }
        : null,
    [owningCampaign],
  );

  // Null included: if the owning campaign disappears on a refetch, a stale
  // clamp must fall back to the locked "dates unavailable" state.
  useEffect(() => {
    if (calendarOwned) {
      storeApi.setState({ scheduleWindow: campaignWindow });
    }
  }, [calendarOwned, campaignWindow, storeApi]);

  return {
    calendarOwned,
    /** Owning campaign's name for the header chip; null until resolved. */
    campaignName: owningCampaign?.name ?? null,
  };
}
